import { ShoppingCart, Bell, MapPin, Menu, Heart, Package, Wallet } from "lucide-react";
import { SMLogo } from "@/app/components/sm-logo";
import { useAccountNavEnabled } from "@/app/context/account-nav-context";

interface CommonHeaderProps {
  isLoggedIn?: boolean;
  userName?: string;
  currentAddress?: string;
  onLoginClick?: () => void;
  /** Sign up (e.g. next to Entrar when logged out). */
  onSignUpClick?: () => void;
  cartCount: number;
  notificationCount?: number;
  /** Favorites count; badge shows 9+ when > 9. */
  favoritesCount?: number;
  onCartClick: () => void;
  onNotificationsClick: () => void;
  onAddressClick: () => void;
  onMenuClick?: () => void;
  onFavoritesClick?: () => void;
  onMyAdsClick?: () => void;
  onWalletClick?: () => void;
  /** Logo (+ title block) goes to app home when set */
  onLogoClick?: () => void;
}

export function CommonHeader({
  isLoggedIn = true,
  userName = "",
  currentAddress = "",
  onLoginClick,
  onSignUpClick,
  cartCount,
  notificationCount = 0,
  favoritesCount = 0,
  onCartClick,
  onNotificationsClick,
  onAddressClick,
  onMenuClick,
  onFavoritesClick,
  onMyAdsClick,
  onWalletClick,
  onLogoClick,
}: CommonHeaderProps) {
  const accountNav = useAccountNavEnabled();

  return (
    <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-3 lg:py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Logo and Address */}
          <div className="flex items-center gap-4 lg:gap-6">
            {/* Mobile menu — hidden when logged out or profile incomplete */}
            {accountNav && (
              <button
                onClick={onMenuClick}
                className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <Menu className="w-6 h-6 text-white" />
              </button>
            )}

            {/* Logo — link to home */}
            {onLogoClick ? (
              <button
                type="button"
                onClick={onLogoClick}
                className="flex items-center gap-3 rounded-xl p-1 -m-1 text-left hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 transition-colors"
                aria-label="Ir para início"
              >
                <SMLogo className="w-10 h-10 lg:w-12 lg:h-12 shrink-0" />
                <div className="hidden sm:block min-w-0">
                  <h1 className="text-lg lg:text-xl text-white font-semibold">akihub</h1>
                  <p className="text-xs text-white/80 hidden lg:block">Conectando quem troca, garantindo quem recebe</p>
                </div>
              </button>
            ) : (
              <div className="flex items-center gap-3">
                <SMLogo className="w-10 h-10 lg:w-12 lg:h-12" />
                <div className="hidden sm:block">
                  <h1 className="text-lg lg:text-xl text-white font-semibold">akihub</h1>
                  <p className="text-xs text-white/80 hidden lg:block">Conectando quem troca, garantindo quem recebe</p>
                </div>
              </div>
            )}
          </div>

          {/* Right: Desktop Navigation + Actions */}
          <div className="flex items-center gap-2 lg:gap-4">
            {/* Desktop hub links — hidden when logged out or profile incomplete */}
            {accountNav && (
              <div className="hidden lg:flex items-center gap-2">
                <button
                  onClick={onWalletClick}
                  className="flex items-center gap-2 px-4 py-2 text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <Wallet className="w-5 h-5" />
                  <span className="text-sm font-medium">Conta Digital</span>
                </button>
                <button
                  onClick={onMyAdsClick}
                  className="flex items-center gap-2 px-4 py-2 text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <Package className="w-5 h-5" />
                  <span className="text-sm font-medium">Meus Anúncios</span>
                </button>
                <button
                  onClick={onFavoritesClick}
                  className="relative flex items-center gap-2 px-4 py-2 text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <Heart className="w-5 h-5" />
                  <span className="text-sm font-medium">Favoritos</span>
                  {favoritesCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-white text-primary text-xs rounded-full min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center font-medium">
                      {favoritesCount > 9 ? "9+" : favoritesCount}
                    </span>
                  )}
                </button>
              </div>
            )}

            {/* Notifications — hidden when logged out or profile incomplete */}
            {accountNav && (
              <button 
                onClick={onNotificationsClick}
                className="relative p-2 lg:p-3 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-lg transition-colors"
              >
                <Bell className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-white text-primary text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                    {notificationCount > 9 ? "9+" : notificationCount}
                  </span>
                )}
              </button>
            )}
            
            {/* Cart — hidden when logged out or profile incomplete */}
            {accountNav && (
              <button 
                onClick={onCartClick}
                className="relative p-2 lg:p-3 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-lg transition-colors"
              >
                <ShoppingCart className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-white text-primary text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                    {cartCount > 9 ? "9+" : cartCount}
                  </span>
                )}
              </button>
            )}

            {/* User Info or Login - visible on all breakpoints so "Entrar" is visible on mobile */}
            <div className="ml-2 min-w-0 max-w-[min(11rem,42vw)] sm:max-w-none shrink text-right sm:text-left">
              {isLoggedIn ? (
                <>
                  <p className="font-medium text-white text-sm leading-tight break-words">{userName || "—"}</p>
                  {/* Address only after onboarding / profile complete (same as hub chrome) */}
                  {accountNav && (
                    <button
                      type="button"
                      onClick={onAddressClick}
                      className="flex items-center gap-1 text-white/80 hover:text-white transition-colors mt-0.5 w-full justify-end sm:justify-start min-w-0"
                    >
                      <MapPin className="w-3 h-3 shrink-0 hidden sm:block" aria-hidden />
                      <span className="text-xs break-words text-right sm:text-left min-w-0">{currentAddress || "—"}</span>
                    </button>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-2 text-sm">
                  {onSignUpClick ? (
                    <button
                      type="button"
                      onClick={onSignUpClick}
                      className="text-white/90 hover:text-white font-medium underline underline-offset-2 py-1"
                    >
                      Cadastrar
                    </button>
                  ) : null}
                  {onSignUpClick && onLoginClick ? (
                    <span className="text-white/50 select-none" aria-hidden>
                      ·
                    </span>
                  ) : null}
                  {onLoginClick ? (
                    <button
                      type="button"
                      onClick={onLoginClick}
                      className="text-white/90 hover:text-white font-medium underline underline-offset-2 py-1"
                    >
                      Entrar
                    </button>
                  ) : null}
                </div>
              )}
            </div>

            {/* Desktop menu — hidden when logged out or profile incomplete */}
            {accountNav && (
              <button
                onClick={onMenuClick}
                className="hidden lg:flex items-center justify-center p-2 lg:p-3 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-lg transition-colors"
              >
                <Menu className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}